export const manufacturingEn = {
  title: 'Production orders',
  subtitle:
    'Plan approved recipes, consume exact lots, and report completion with a complete audit trail.',
  help: {
    open: 'Open production-order help',
    detail: 'Open help for planning, postings, and completion',
  },
  actions: {
    new: 'New order',
    plan: 'Plan',
    release: 'Release',
    reserve: 'Reserve material',
    start: 'Start',
    pause: 'Pause',
    resume: 'Resume',
    reportComplete: 'Report complete',
    close: 'Close',
    cancel: 'Cancel order',
    issue: 'Issue',
    return: 'Return',
    confirmStep: 'Confirm',
    comment: 'Comment',
    post: 'Post',
  },
  filters: {
    search: 'Search number, product, or demand',
    status: 'Status',
    allStatuses: 'All statuses',
    priority: 'Priority',
    allPriorities: 'All priorities',
    from: 'Start from',
    to: 'Start to',
  },
  statuses: {
    DRAFT: 'Draft',
    PLANNED: 'Planned',
    RELEASED: 'Released',
    MATERIAL_RESERVED: 'Material reserved',
    IN_PRODUCTION: 'In production',
    PAUSED: 'Paused',
    REPORTED_COMPLETE: 'Reported complete',
    CLOSED: 'Closed',
    CANCELLED: 'Cancelled',
  },
  priorities: { LOW: 'Low', NORMAL: 'Normal', HIGH: 'High', URGENT: 'Urgent' },
  columns: {
    number: 'Order',
    product: 'Finished product',
    status: 'Status',
    priority: 'Priority',
    recipe: 'Recipe',
    planned: 'Planned',
    produced: 'Produced',
    start: 'Start',
    updated: 'Updated',
    position: 'Pos.',
    material: 'Material',
    reserved: 'Reserved',
    issued: 'Issued',
    returned: 'Returned',
    scrap: 'Scrap',
    variance: 'Variance',
    step: 'Operation',
    workCenter: 'Work centre',
    duration: 'Duration (min)',
    input: 'Input',
    output: 'Output',
    type: 'Type',
    batch: 'Lot',
    quantity: 'Quantity',
  },
  empty: 'No production orders found.',
  editor: {
    newTitle: 'Create production order',
    subtitle: 'Server-side batch scaling derives requirements from an approved recipe and routing.',
    plan: 'Planning basis',
    planDescription:
      'Choose source, quantity, dates, warehouses, and owners. Drafts use optimistic locking.',
  },
  fields: {
    recipe: 'Approved recipe',
    routing: 'Approved routing',
    plannedQuantity: 'Planned quantity',
    unit: 'Unit',
    priority: 'Priority',
    salesOrder: 'Sales order',
    plannedStart: 'Planned start',
    plannedEnd: 'Planned end',
    sourceWarehouse: 'Source warehouse',
    targetWarehouse: 'Target warehouse',
    internalDemand: 'Internal demand',
    responsibleUsers: 'Responsible users',
    note: 'Note / reason',
    postingType: 'Posting type',
    quantity: 'Quantity',
    reason: 'Reason',
    stepResult: 'Result',
    inputQuantity: 'Input quantity',
    outputQuantity: 'Output quantity',
    scrapQuantity: 'Scrap quantity',
    duration: 'Actual duration (min)',
    qualityChecks: 'Required quality checks',
    producedQuantity: 'Produced quantity',
    batchNumber: 'New lot number',
    bestBefore: 'Best-before date',
  },
  shortage: {
    title: 'Material shortage',
    description: '{{count}} material item(s) cannot be fully covered by current free stock.',
  },
  tabs: {
    materials: 'Materials & consumption',
    process: 'Process & quality',
    outputs: 'Outputs',
    history: 'History & comments',
    documents: 'DMS documents',
  },
  documents: { title: 'Production record' },
  summary: {
    planned: 'Planned quantity',
    produced: 'Produced',
    scrap: 'Scrap',
    yield: 'Actual yield',
    plannedCost: 'Planned material cost',
    actualCost: 'Actual material cost',
  },
  materials: {
    title: 'Material items',
    description:
      'Requirements, reservations, and lot-level postings remain separate and use exact decimals.',
  },
  process: {
    title: 'Process confirmations',
    description:
      'Operations, parameters, and quality checks come from the approved planning snapshot.',
  },
  stepStatuses: {
    PENDING: 'Pending',
    IN_PROGRESS: 'In progress',
    COMPLETED: 'Completed',
    SKIPPED: 'Skipped with reason',
  },
  outputs: {
    title: 'Output and produced lots',
    description: 'Finished goods, by-products, and scrap from completion.',
    empty: 'No output has been posted.',
  },
  outputTypes: { FINISHED_GOOD: 'Finished good', BYPRODUCT: 'By-product', SCRAP: 'Scrap' },
  history: {
    title: 'Immutable posting history',
    description: 'Every operation records an idempotency key, payload hash, user, and timestamp.',
  },
  comments: {
    title: 'Comments',
    description: 'Append-only notes in the audited production record.',
    empty: 'No comments yet.',
  },
  operationTypes: {
    CREATE: 'Created',
    UPDATE: 'Draft updated',
    PLAN: 'Planned',
    RELEASE: 'Released',
    RESERVE: 'Reserved',
    ISSUE: 'Material issued',
    RETURN: 'Material returned',
    START: 'Started',
    PAUSE: 'Paused',
    RESUME: 'Resumed',
    STEP_CONFIRM: 'Step confirmed',
    REPORT_COMPLETE: 'Reported complete',
    CLOSE: 'Closed',
    CANCEL: 'Cancelled',
    COMMENT: 'Comment added',
  },
  workflow: {
    plan: {
      title: 'Plan order',
      description:
        'The planning snapshot is frozen; structural changes are no longer possible afterwards.',
    },
    release: {
      title: 'Release order',
      description: 'Approved sources and planned requirements become the binding posting basis.',
    },
    start: {
      title: 'Start production',
      description: 'The actual start is recorded and lot-level issues remain traceable.',
    },
    pause: {
      title: 'Pause production',
      description: 'The order remains postable but is visibly paused.',
    },
    resume: { title: 'Resume production', description: 'The order returns to active production.' },
    close: {
      title: 'Close order',
      description: 'Open reservations are released and the closed order becomes immutable.',
    },
  },
  reserve: {
    title: 'Reserve free stock automatically',
    description:
      'Stock is selected transactionally by item strategy (FEFO/FIFO); shortages remain visible.',
  },
  materialDialog: {
    ISSUE: { title: 'Post material issue' },
    SCRAP: { title: 'Post material scrap' },
    RETURN: { title: 'Post material return' },
  },
  materialActions: { ISSUE: 'Issue', SCRAP: 'Scrap', RETURN: 'Return' },
  quality: { PASS: 'Passed', FAIL: 'Failed', NOT_CHECKED: 'Not checked' },
  stepDialog: { title: 'Confirm process step' },
  report: {
    title: 'Report production complete',
    description:
      'The finished-goods lot and stock receipts are created together in one transaction.',
  },
  cancel: {
    title: 'Cancel production order',
    description:
      'Cancellation requires a reason and is possible only without remaining actual consumption.',
  },
  comment: {
    title: 'Add comment',
    description: 'The entry is stored immutably with user and timestamp.',
  },
  messages: {
    created: 'Production order created.',
    saved: 'Draft saved.',
    workflow: 'Status updated.',
    reserved: 'Available material reserved.',
    posted: 'Material posting completed.',
    stepConfirmed: 'Process step confirmed.',
    completed: 'Production reported complete.',
    cancelled: 'Production order cancelled.',
    commented: 'Comment added.',
  },
} as const;
